/** @format */

import React from "react";
import { Box } from "@mui/material";
import { EmailOutlined, SmsOutlined } from "@mui/icons-material";

// ─────────────────────────────────────────────────────────────────────────
// ChannelBadge — tiny pill that labels which channel a customer message
// came through (Email vs SMS). Used in the replies inbox, the per-invoice
// replies list, and the unified activity feed so operators can tell at a
// glance how a message reached them.
// ─────────────────────────────────────────────────────────────────────────

type Channel = "email" | "sms" | string | undefined;

const TONE: Record<string, { bg: string; fg: string }> = {
    email: { bg: "rgba(59,130,246,0.14)", fg: "#1e40af" },
    sms: { bg: "rgba(30,193,149,0.16)", fg: "#0f7a5b" },
};

const ChannelBadge: React.FC<{ channel: Channel; size?: "sm" | "xs" }> = ({
    channel,
    size = "sm",
}) => {
    if (channel !== "email" && channel !== "sms") return null;
    const tone = TONE[channel];
    const isSms = channel === "sms";
    const iconSize = size === "xs" ? 12 : 14;

    return (
        <Box
            component="span"
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                fontFamily: "'Montserrat', sans-serif",
                fontSize: size === "xs" ? "0.65rem" : "0.7rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                padding: size === "xs" ? "2px 6px" : "3px 8px",
                borderRadius: "999px",
                backgroundColor: tone.bg,
                color: tone.fg,
                lineHeight: 1.2,
            }}
        >
            {isSms ? (
                <SmsOutlined sx={{ fontSize: iconSize }} />
            ) : (
                <EmailOutlined sx={{ fontSize: iconSize }} />
            )}
            {isSms ? "SMS" : "Email"}
        </Box>
    );
};

export default ChannelBadge;
